import { useState, useCallback, useEffect, useRef } from 'react'
import { GoogleMap, LoadScript, InfoWindow } from '@react-google-maps/api'
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { useAllClinics } from '@/hooks/useAllClinics'
import { Building2, MapPin, Phone, Clock } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import BackButton from '@/components/warRoom/BackButton'
import ClinicStatsCard from '@/components/warRoom/ClinicStatsCard'
import AppVersionCard from '@/components/warRoom/AppVersionCard'
import RightSidePanel from '@/components/warRoom/RightSidePanel'

const mapContainerStyle = {
  width: '100%',
  height: '100vh',
}

// 台灣的中心點
const defaultCenter = {
  lat: 23.9739,
  lng: 120.9794,
}

// 自定義地圖樣式（簡潔風格）
const mapStyles = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
]

interface ClinicMarker {
  clinic_id: number
  clinic_name: string
  address: string
  phone?: string
  lat: number
  lng: number
  is_active: boolean
}

const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = ['places']

export default function WarRoomPage() {
  const { clinics, isLoading } = useAllClinics()
  const [selectedClinic, setSelectedClinic] = useState<ClinicMarker | null>(null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null)
  const markersRef = useRef<google.maps.Marker[]>([])

  // 過濾有經緯度的診所
  const clinicsWithLocation = clinics
    .filter((clinic) => clinic.lat && clinic.lng)
    .map((clinic) => ({
      clinic_id: clinic.clinic_id,
      clinic_name: clinic.clinic_name,
      address: clinic.address || '',
      phone: clinic.phone,
      lat: parseFloat(clinic.lat!),
      lng: parseFloat(clinic.lng!),
      is_active: clinic.is_active,
    }))

  // 自定義標記圖標
  const createMarkerIcon = (isActive: boolean): google.maps.Icon => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 4C14.477 4 10 8.477 10 14c0 7.5 10 22 10 22s10-14.5 10-22c0-5.523-4.477-10-10-10zm0 14c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" 
              fill="${isActive ? '#10b981' : '#6b7280'}" 
              stroke="#ffffff" 
              stroke-width="2"/>
      </svg>
    `)}`,
    scaledSize: new google.maps.Size(40, 40),
    anchor: new google.maps.Point(20, 40),
  })

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map)
  }, [])

  const onUnmount = useCallback(() => {
    setMap(null)
    clusterer?.clearMarkers()
  }, [clusterer])

  // 當地圖和診所資料都準備好時，創建標記和群集
  useEffect(() => {
    if (!map || !clinicsWithLocation.length) return

    // 清除舊的標記
    markersRef.current.forEach((marker) => marker.setMap(null))
    markersRef.current = []

    // 創建新的標記
    const markers = clinicsWithLocation.map((clinic) => {
      const marker = new google.maps.Marker({
        position: { lat: clinic.lat, lng: clinic.lng },
        icon: createMarkerIcon(clinic.is_active),
        title: clinic.clinic_name,
      })

      marker.addListener('click', () => {
        setSelectedClinic(clinic)
      })

      markersRef.current.push(marker)
      return marker
    })

    // 創建或更新群集
    if (clusterer) {
      clusterer.clearMarkers()
      clusterer.addMarkers(markers)
    } else {
      const newClusterer = new MarkerClusterer({
        map,
        markers,
        renderer: {
          render: ({ count, position }) => {
            const svg = `
              <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="28" fill="#3b82f6" stroke="#ffffff" stroke-width="3"/>
                <text x="30" y="36" font-size="18" font-weight="bold" text-anchor="middle" fill="#ffffff">${count}</text>
              </svg>
            `
            return new google.maps.Marker({
              position,
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
                scaledSize: new google.maps.Size(60, 60),
                anchor: new google.maps.Point(30, 30),
              },
              title: `${count} 間診所`,
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            })
          },
        },
        algorithm: new SuperClusterAlgorithm({
          radius: 80,
          maxZoom: 15,
        }),
      })
      setClusterer(newClusterer)
    }
  }, [map, clinicsWithLocation, clusterer])

  if (isLoading) {
    return (
      <div className="relative w-full h-screen">
        <Skeleton className="w-full h-full" />
        <div className="fixed top-4 left-4 z-10 space-y-3">
          <Skeleton className="h-11 w-20 rounded-lg" />
          <ClinicStatsCard totalClinics={0} clinicsOnMap={0} isLoading />
        </div>
        <AppVersionCard />
      </div>
    )
  }

  return (
    <div className="relative w-full h-screen">
      {/* 控制面板區域 - 包含返回按鈕和統計卡片 */}
      <div className="fixed top-4 left-4 z-10 space-y-3">
        <BackButton />
        <ClinicStatsCard totalClinics={clinics.length} clinicsOnMap={clinicsWithLocation.length} />
      </div>

      {/* Google 地圖 */}
      <LoadScript
        googleMapsApiKey={
          import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyCf9t5vusDp7BX-1-vj7D1Xkhz8QRPhyzc'
        }
        libraries={libraries}
        language="zh-TW"
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={7}
          center={defaultCenter}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            styles: mapStyles,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          }}
        >
          {/* 資訊視窗 */}
          {selectedClinic && (
            <InfoWindow
              position={{ lat: selectedClinic.lat, lng: selectedClinic.lng }}
              onCloseClick={() => setSelectedClinic(null)}
            >
              <div className="p-3 min-w-[250px]">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-600" />
                  {selectedClinic.clinic_name}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{selectedClinic.address}</span>
                  </div>
                  {selectedClinic.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{selectedClinic.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span
                      className={`font-medium ${selectedClinic.is_active ? 'text-green-600' : 'text-gray-500'}`}
                    >
                      {selectedClinic.is_active ? '營業中' : '已停業'}
                    </span>
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>

      {/* APP 版本更新狀態卡片 */}
      <AppVersionCard />

      {/* 右側面板 */}
      <RightSidePanel />
    </div>
  )
}
